import React from 'react';
import { styled, css, useTheme } from 'styled-components';

import { Media } from '../../theme/Media.tsx';
import { Tooltip } from '../../V2/Feedback/Tooltip/index.tsx';
import { useTableContext } from './context.tsx';
import MobileLabel from './MobileLabel.tsx';
import type { CellContentAlign } from './types.ts';

export const TooltipWrapper = styled.span`
  font-size: 13px;
`;

const StatusIndicator = styled.span<{ $statusColor?: string; $isCollapsedNav?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  @media ${({ $isCollapsedNav }) => ($isCollapsedNav ? Media.lgMin : Media.xlgMin)} {
    width: 4px;
    bottom: 0;
  }
  @media ${({ $isCollapsedNav }) => ($isCollapsedNav ? Media.mdMax : Media.lgMax)} {
    right: 0;
    height: 4px;
  }
  background-color: ${({ $statusColor }) => $statusColor};
`;

const StatusIndicatorCover = styled(StatusIndicator)`
  z-index: 1;
  @media ${({ $isCollapsedNav }) => ($isCollapsedNav ? Media.lgMin : Media.xlgMin)} {
    width: 10px;
  }
`;

interface IWrapper {
  $bgColor?: string;
  $subCell?: boolean;
  $actionCell?: boolean;
  $md?: 1 | 2 | 3 | 4;
  $sm?: 1 | 2 | 3 | 4;
  $wordBreak?: 'phone' | 'all';
  $isCollapsedNav?: boolean;
  $mainCell?: boolean;
  $nowrap?: boolean;
  $width?: number;
}

export const Wrapper = styled.td<IWrapper>`
  position: relative;
  background: ${({ $bgColor }) => $bgColor};
  transition: background 0.3s;
  border-bottom: ${({ $subCell, theme }) => !$subCell && `1px solid ${theme.colors.grey10_1}`};
  white-space: ${({ $nowrap }) => $nowrap && 'nowrap'};
  ${({ $wordBreak }) =>
    $wordBreak === 'all' &&
    css`
      word-break: break-all;
    `}
  ${({ $wordBreak }) =>
    $wordBreak === 'phone' &&
    css`
      @media ${Media.xsMax} {
        word-break: break-all;
      }
    `}
  ${({ $actionCell, $isCollapsedNav }) =>
    $actionCell &&
    css`
      @media ${$isCollapsedNav ? Media.lgMin : Media.xlgMin} {
        width: 1px;
      }
    `}
  ${({ $actionCell, $isCollapsedNav, $mainCell }) =>
    !$actionCell &&
    $mainCell &&
    css`
      @media ${$isCollapsedNav ? Media.lgMin : Media.xlgMin} {
        width: 100%;
      }
    `}
  ${({ $actionCell, $isCollapsedNav, $mainCell, $width }) =>
    !$actionCell &&
    !$mainCell &&
    $width &&
    css`
      @media ${$isCollapsedNav ? Media.lgMin : Media.xlgMin} {
        width: ${`${$width}px`};
      }
    `}
  @media ${Media.smMax} {
    ${({ $sm }) =>
      $sm &&
      css`
        width: ${100 / $sm}%!important;
      `};
  }
  @media ${({ $isCollapsedNav }) => ($isCollapsedNav ? Media.mdMax : Media.lgMax)} {
    display: flex;
    width: ${({ $md }) => ($md ? `${100 / $md}%!important` : '100%!important')};
    ${({ $actionCell }) =>
      $actionCell &&
      css`
        position: absolute;
        background: transparent;
        top: 0;
        right: 0;
        border: none;
        width: 36px !important;
        height: 36px;
        z-index: 1;
      `};
  }
`;

interface IContainer {
  $inline?: boolean;
  $actionCell?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  $minWidth?: number;
  $subCell?: boolean;
  $initialCell?: boolean;
  $padding?: string;
  $isCollapsedNav?: boolean;
  $align?: CellContentAlign;
}

export const Container = styled.div<IContainer>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${({ $padding, $actionCell, $subCell, $initialCell }) =>
    $padding
      ? css`
          padding: ${$padding};
        `
      : css`
          padding: ${!$actionCell && !$subCell && !$initialCell && '14px'};
        `}
  position: relative;
  cursor: ${({ onClick }) => onClick && 'pointer'};
  @media ${({ $isCollapsedNav }) => ($isCollapsedNav ? Media.lgMin : Media.xlgMin)} {
    min-height: ${({ $subCell, $initialCell }) => !$subCell && !$initialCell && '60px'};
    align-items: ${({ $actionCell }) => $actionCell && 'flex-end'};
    ${({ $align }) =>
      $align &&
      css`
        align-items: ${$align === 'left' && 'flex-start'};
        align-items: ${$align === 'center' && 'center'};
        align-items: ${$align === 'right' && 'flex-end'};
      `}
    min-width: ${({ $actionCell }) => $actionCell && '46px'};
    min-width: ${({ $minWidth }) => $minWidth && `${$minWidth}px`};
  }
  @media ${({ $isCollapsedNav }) => ($isCollapsedNav ? Media.mdMax : Media.lgMax)} {
    justify-content: flex-start;
    width: 100%;
    min-height: 36px;
    ${({ $inline }) =>
      $inline &&
      css`
        flex-direction: row;
        align-items: center;
      `}
  }
  @media ${Media.smMax} {
    min-height: ${({ $actionCell }) => $actionCell && 'unset'};
  }
`;

export interface ITd {
  id?: string;
  children?: React.ReactNode;
  className?: string;
  colSpan?: number;
  bgColor?: string;
  title?: string;
  inline?: boolean;
  type?: 'action' | 'sub' | 'initial';
  onClick?: (e: React.MouseEvent) => void;
  minWidth?: number;
  statusColor?: string;
  statusTooltip?: React.ReactNode;
  dataTestId?: string;
  md?: 1 | 2 | 3 | 4;
  sm?: 1 | 2 | 3 | 4;
  padding?: string;
  wordBreak?: 'all' | 'phone';
  align?: 'left' | 'center' | 'right';
  mainCell?: boolean;
  $nowrap?: boolean;
  $width?: number;
}

const Td = React.forwardRef<HTMLTableCellElement, ITd & { ref?: React.Ref<HTMLTableCellElement> }>(function Td(
  {
    title,
    children,
    className,
    colSpan,
    bgColor,
    inline,
    type,
    onClick,
    minWidth,
    statusColor,
    statusTooltip,
    dataTestId,
    md,
    sm,
    padding,
    align,
    mainCell,
    $nowrap,
    $width,
    wordBreak,
  },
  ref,
) {
  const theme = useTheme();
  const { isCollapsedNav } = useTableContext();
  return (
    <Wrapper
      className={className}
      colSpan={type === 'sub' ? 1000 : colSpan}
      $bgColor={bgColor ?? theme.colors.grey00_1}
      $subCell={type === 'sub'}
      $actionCell={type === 'action'}
      data-testid={dataTestId}
      $md={md}
      $sm={sm}
      $isCollapsedNav={isCollapsedNav}
      $mainCell={mainCell}
      $nowrap={$nowrap}
      $width={$width}
      $wordBreak={wordBreak}
      ref={ref}>
      {statusColor && statusTooltip && (
        <Tooltip
          tooltip={<TooltipWrapper>{statusTooltip}</TooltipWrapper>}
          ContainerComponent={StatusIndicatorCover}
          placement='right'
          containerComponentProps={{
            $isCollapsedNav: isCollapsedNav,
          }}>
          <StatusIndicator $statusColor={statusColor} $isCollapsedNav={isCollapsedNav} />
        </Tooltip>
      )}
      {statusColor && !statusTooltip && <StatusIndicator $statusColor={statusColor} $isCollapsedNav={isCollapsedNav} />}
      <Container
        onClick={onClick}
        $inline={inline}
        $actionCell={type === 'action'}
        $minWidth={minWidth}
        $subCell={type === 'sub'}
        $padding={padding}
        $initialCell={type === 'initial'}
        $align={align}
        $isCollapsedNav={isCollapsedNav}>
        {title && <MobileLabel $inline={inline}>{title}</MobileLabel>}
        {children}
      </Container>
    </Wrapper>
  );
});

export default React.memo(Td);
